import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { PrimaryMarketProject, Reservation, ReservationStatus, StatusKey } from 'api-client';
import {
  AccountNotReadyWarningSection,
  CapitalProtectionBanner,
  DataTable,
  Pagination,
} from 'components';
import ProjectsFilter, {
  Filter,
} from 'components/pages/projects/filter/ProjectsFilter/ProjectsFilter';
import { defaultProjectsFilter } from 'components/pages/projects/filter/ProjectsFilter/utils';
import { ProjectsTableFilterBar } from 'components/pages/projects/filter/ProjectsTableFilterBar';
import { groupPrimaryMarketProjects } from 'components/pages/projects/primary-market/groupProjects';
import { PrimaryMarketGroupMobileCard } from 'components/pages/projects/primary-market/PrimaryMarketGroupMobileCard';
import { PrimaryMarketGroupRow } from 'components/pages/projects/primary-market/PrimaryMarketGroupRow';
import { PrimaryMarketMobileCard } from 'components/pages/projects/primary-market/PrimaryMarketMobileCard';
import { PrimaryMarketTableRow } from 'components/pages/projects/primary-market/PrimaryMarketTableRow';
import {
  DEFAULT_STATUS_KEY,
  getStatusKeyByStatuses,
  statusTables,
} from 'components/pages/projects/primary-market/statusTables';
import { RoiIrrHint } from 'components/pages/projects/RoiIrrHint';
import { PaginationLoadMore } from 'components/pagination';
import { useErrorCallback, useToggle } from 'hooks';

import { ProjectsCount } from '../../../../../../api-client/models/project/ProjectsCount';
import download from '../../../../../../assets/images/svg/download.svg';
import OnboardingModal from '../../../../../../components/onboarding/OnboardingModal/OnboardingModal';
import apiClientConfig from '../../../../../../configs/app';
import {
  createProfileManager,
  createProjectProvider,
  createReservationManager,
} from '../../../../../../di';
import { ymAsync } from '../../../../../../utils/functions/ymWrapper';
import ProjectsContextProvider, { ProjectsContextType } from '../ProjectsContext';

import './PrimaryMarket.scoped.scss';

export interface AutoInvestmentFilter {
  projectUuids?: Array<string>;
}

const DEFAULT_PER_PAGE = 20;

const initialFilter: Filter = {
  ...defaultProjectsFilter,
  statuses: statusTables[DEFAULT_STATUS_KEY].statuses,
};

const PrimaryMarket = () => {
  const handleError = useErrorCallback();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [onboardingPassed, setOnboardingPassed] = useState(true);
  const [projectsCount, setProjectsCount] = useState<ProjectsCount>({
    closed: 0,
    current: 0,
    fundRaising: 0,
    autoInvestment: 0,
    refunded: 0,
  });
  const [projects, setProjects] = useState<Array<PrimaryMarketProject>>([]);
  const [reservations, setReservations] = useState<Map<string, Reservation>>(new Map());
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [filterActive, toggleFilter] = useToggle();

  const [filter, setFilter] = useState<Filter>(initialFilter);

  const statusKey: StatusKey = getStatusKeyByStatuses(filter.statuses);
  const statusTable = statusTables[statusKey];

  // projects остаётся плоским: PaginationLoadMore считает загруженное по projects.length
  // против totalCount, а totalCount с бэкенда — это количество проектов, не строк.
  // Поэтому группировка живёт на отрисовке.
  const rows = useMemo(() => groupPrimaryMarketProjects(projects), [projects]);

  const value: ProjectsContextType = {
    filter,
    setFilter,
    filterActive,
    toggleFilter,
  };

  const loadReservations = async (): Promise<void> => {
    try {
      const reservationManager = await createReservationManager();
      const list = await reservationManager.getAll();
      const active = list.filter((item) => item.status !== ReservationStatus.COMPLETED);
      setReservations(new Map(active.map((item) => [item.projectUuid, item])));
    } catch (err) {
      if (err instanceof Error) await handleError(err);
    }
  };

  const loadProjects = useCallback(
    async (pageToLoad: number, pageSize: number, append: boolean): Promise<void> => {
      append ? setLoadingMore(true) : setLoading(true);
      try {
        const provider = await createProjectProvider();
        const collection = await provider.getPrimaryMarketProjects(pageToLoad, pageSize, filter);
        setProjects((prev) => (append ? [...prev, ...collection.items] : collection.items));
        setTotalCount(collection.totalCount);
        setPage(pageToLoad);

        if (!append) {
          const profileManager = await createProfileManager();
          const isOnboardingPassed = await profileManager.onboardingPassed();
          setOnboardingPassed(isOnboardingPassed);
          const projectsCountResponse = await provider.getProjectsCount();
          setProjectsCount(projectsCountResponse);
          await loadReservations();
          ymAsync('tab-all-projects');
        }
      } catch (err) {
        if (err instanceof Error) await handleError(err);
      } finally {
        append ? setLoadingMore(false) : setLoading(false);
      }
    },
    [filter]
  );

  const loadData = useCallback(
    async (_page: number, pageSize: number): Promise<void> => {
      setPerPage(pageSize);
      await loadProjects(1, pageSize, false);
    },
    [loadProjects]
  );

  const handleLoadMore = (): void => {
    loadProjects(page + 1, perPage, true);
  };

  const handleReset = (): void => {
    loadProjects(1, perPage, false);
  };

  useEffect(() => {
    if (!onboardingPassed) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [onboardingPassed]);

  const exportStatistics = async (): Promise<void> => {
    setExportLoading(true);
    try {
      const provider = await createProjectProvider();
      const response = await provider.exportPrimaryMarketStatistics(1, totalCount, filter);
      window.location.assign(apiClientConfig.apiBaseUrl + response.url);
    } catch (err) {
      if (err instanceof Error) await handleError(err);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Pagination
        onLoad={loadData}
        count={projects.length}
        totalCount={totalCount}
        filterObject={filter}
        initialChangeFilter={(value: Partial<Filter>) => setFilter({ ...filter, ...value })}
      >
        <AccountNotReadyWarningSection />
        <OnboardingModal
          onClose={() => false}
          onNextStep={() => true}
          isOpen={!onboardingPassed}
          step={1}
        >
          <p>Дополнительная информация, отображаемая в модальном окне.</p>
        </OnboardingModal>
        <ProjectsContextProvider props={value}>
          <div className="primary-market">
            <CapitalProtectionBanner className="primary-market__promo" />
            <div className="primary-market__toolbar">
              <ProjectsTableFilterBar
                projectsCount={projectsCount}
                withAllStatuses={false}
                onExport={exportStatistics}
                exportLoading={exportLoading}
                exportIconSrc={download}
              />
              <ProjectsFilter />
            </div>
            <Pagination.Content loading={loading}>
              <div className="primary-market__desktop">
                <DataTable
                  variant="surface"
                  columns={statusTable.gridColumns}
                  className="primary-market__table"
                >
                  <DataTable.Head>
                    {statusTable.columns.map((column, index) => (
                      <DataTable.HeadCell
                        key={`${column.label}-${index}`}
                        align={column.align}
                        className={column.nowrap ? 'primary-market__head-cell--nowrap' : undefined}
                      >
                        {column.withRoiIrrHint ? (
                          <RoiIrrHint>{column.label}</RoiIrrHint>
                        ) : (
                          column.label
                        )}
                      </DataTable.HeadCell>
                    ))}
                  </DataTable.Head>
                  <DataTable.Body>
                    {rows.length === 0 ? (
                      <DataTable.Empty>{statusTable.emptyText}</DataTable.Empty>
                    ) : (
                      rows.map((row) =>
                        row.kind === 'group' ? (
                          <PrimaryMarketGroupRow
                            group={row.group}
                            members={row.members}
                            statusKey={statusKey}
                            key={row.group.uuid}
                          />
                        ) : (
                          <PrimaryMarketTableRow
                            project={row.project}
                            statusKey={statusKey}
                            key={row.project.uuid}
                          />
                        )
                      )
                    )}
                  </DataTable.Body>
                </DataTable>
              </div>
              <div className="primary-market__mobile">
                {rows.length === 0 ? (
                  <div className="primary-market__empty">{statusTable.emptyText}</div>
                ) : (
                  rows.map((row) =>
                    row.kind === 'group' ? (
                      <PrimaryMarketGroupMobileCard
                        group={row.group}
                        members={row.members}
                        statusKey={statusKey}
                        reservations={reservations}
                        key={row.group.uuid}
                      />
                    ) : (
                      <PrimaryMarketMobileCard
                        project={row.project}
                        statusKey={statusKey}
                        reservation={reservations.get(row.project.uuid)}
                        key={row.project.uuid}
                      />
                    )
                  )
                )}
              </div>
              <PaginationLoadMore
                loaded={projects.length}
                total={totalCount}
                page={page}
                loading={loadingMore}
                onLoadMore={handleLoadMore}
                onReset={handleReset}
              />
            </Pagination.Content>
          </div>
        </ProjectsContextProvider>
      </Pagination>
    </>
  );
};

export default PrimaryMarket;
